import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { SmartMeterFormValues } from '../../components/SystemConfiguration/SmartMeter/SmartMeterForm'
import { object, string, boolean, ObjectSchema } from 'yup'
import moment from 'moment'
import { fetchCurrentAndNextYearHolidayDates, isDateWeekend } from '../../utils/desired-shipping-date'
import { getDay, isBefore, isSameDay, isValid, parseISO, startOfDay } from '@e3dc-react/shell/Libs/date'
import { DayOfWeek } from '../../types'

/*
 * This Hoook Holds SmartMeter validation schema and initial values
 */
const useSmartMeterValidation = (): {
  initialValues: SmartMeterFormValues
  validationSchema: ObjectSchema<SmartMeterFormValues>
} => {
  const { t } = useCustomTranslation()
  const availableHolidays = fetchCurrentAndNextYearHolidayDates()
  /* 6 week + 1 day since the smart meter order can happend only after 6 weeks */
  const sixWeeksFromCurrentDate = moment().startOf('day').add(6, 'weeks').add(1, 'day').format('YYYY-MM-DD')

  const initialValues: SmartMeterFormValues = {
    firstName: '',
    lastName: '',
    street: '',
    streetNumber: '',
    zipCode: '',
    city: '',
    email: '',
    mechanicName: '',
    previousMeterNumber: '',
    targetInstallationDate: sixWeeksFromCurrentDate,
    targetInstallationTime: '',
    administrationNumber: '',
    confirmCustomerData: false,
  }

  const validationSchema = object().shape({
    firstName: string().required(t('validations.form.first-name.is-required')),
    lastName: string().required(t('validations.form.last-name.is-required')),
    street: string().required(t('validations.form.street.is-required')),
    streetNumber: string().required(t('validations.form.street-number.is-required')),
    zipCode: string().required(t('validations.form.zip-code.is-required')),
    city: string().required(t('validations.form.city.is-required')),
    email: string().required(t('validations.form.email.is-required')),
    mechanicName: string().required(t('validations.form.mechanich-name.is-required')),
    previousMeterNumber: string().required(t('validations.form.previous-meter-number.is-required')),
    targetInstallationDate: string()
      .required(t('validations.form.target-installation-date.is-required'))
      .test('valid-date', t('validations.form.target-installation-date.invalid-date'), value => {
        if (!value) return false

        const date = parseISO(value)

        if (!isValid(date)) return false

        if (isBefore(startOfDay(date), startOfDay(new Date()))) return false

        if (isDateWeekend(date)) return false

        if (getDay(date) === DayOfWeek.Monday) return false

        if (availableHolidays.some(holiday => isSameDay(holiday, date))) {
          return false
        }

        return true
      }),
    targetInstallationTime: string().required(t('validations.form.target-installation-time.is-required')),
    administrationNumber: string(),
    confirmCustomerData: boolean()
      .oneOf([true], t('validations.form.confirm-customer-data.is-required'))
      .required(t('validations.form.confirm-customer-data.is-required')),
  })
  return { validationSchema, initialValues }
}

export default useSmartMeterValidation
